import type { Program, Olympiad, Profile, ProgramEntry } from "@/types/program";
import type { WorkBook } from "xlsx";
import * as XLSX from "xlsx";

export interface AchievementsPayload {
  programsData: Program[];
  olympiadsData: Olympiad[];
  profilesData: Profile[];
  programsList: ProgramEntry[];
  combinedData: (Program | Olympiad)[];
}

function sheetToRows(workbook: WorkBook, sheetName: string) {
  const sheet = workbook.Sheets[sheetName];
  if (!sheet) {
    throw new Error(`Sheet "${sheetName}" not found`);
  }
  return XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet);
}

export function parseAchievementsWorkbook(workbook: WorkBook): AchievementsPayload {
  const achievementsRaw = sheetToRows(workbook, "BasicAchievements");
  const programsData = achievementsRaw
    .map((row) => ({
      title: String(row["TITLE"] ?? ""),
      points: row["SCORE"] as number | undefined,
      campus: String(row["CAMPUS"] ?? ""),
      program_name: String(row["PROGRAM_NAME"] ?? ""),
      details: row["NOTES"] as string | undefined,
      category: row["Category"] as string | undefined,
    }))
    .filter((row) => row.title && row.campus && row.program_name);

  const olympiadsRaw = sheetToRows(workbook, "Olympiads");
  const olympiadsBasic = olympiadsRaw.map((row) => ({
    name: String(row["Name"] ?? ""),
    campus: "Все кампусы",
    type: row["Type"] as string | undefined,
  }));

  const profilesRaw = sheetToRows(workbook, "Profiles");
  const profilesData: Profile[] = profilesRaw.map((row) => ({
    name: String(row["Name"] ?? ""),
    profile: String(row["Profile"] ?? ""),
    level: Number(row["Level"] ?? 0),
  }));

  const olympiadsData: Olympiad[] = olympiadsBasic.map((olympiad) => {
    const profiles = profilesData.filter((profile) => profile.name === olympiad.name);
    return {
      ...olympiad,
      profiles,
      title: olympiad.name,
      details: profiles.map((profile) => `${profile.profile} (уровень ${profile.level})`).join(", "),
    };
  });

  const programsRaw = sheetToRows(workbook, "Programs");
  const programsList: ProgramEntry[] = programsRaw
    .filter((row) => row && typeof row === "object" && (row["Title"] || row["TITLE"]))
    .map((row) => ({
      title: String(row["Title"] || row["TITLE"] || ""),
      campus: String(row["Campus"] || row["CAMPUS"] || ""),
      megaTag: String(row["MegaTag"] || row["MEGATAG"] || ""),
      benefits: String(row["Benefits"] || row["BENEFITS"] || ""),
    }));

  return {
    programsData,
    olympiadsData,
    profilesData,
    programsList,
    combinedData: [...programsData, ...olympiadsData],
  };
}
